import React, { useEffect, useState } from 'react';
import { PixelBox, PixelButton } from '../components/ui/PixelComponents';
import { LoginForm } from '../components/auth/LoginForm';
import { RegisterForm } from '../components/auth/RegisterForm';
import { RecoveryForm } from '../components/auth/RecoveryForm';
import { useAuth } from '../context/AuthContext';

import CrownImage from '../assets/crown.png';
import GhostImage from '../assets/ghost.png';
import MarioImage from '../assets/mario.png';
import ThunderballImage from '../assets/thunderball.png';
import HeartImage from '../assets/heart.png';
import HandImage from '../assets/hand.png';

type AuthMode = 'home' | 'login' | 'register' | 'recovery';

export const HomePage: React.FC = () => {
  const [authMode, setAuthMode] = useState<AuthMode>('home');
  const [isRegistrationComplete, setIsRegistrationComplete] = useState(false);
  const { register } = useAuth();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    let inviteCode = urlParams.get('inviteCode') || urlParams.get('invite');

    if (!inviteCode) {
      const hash = window.location.hash;
      const match = hash.match(/invite=([a-f0-9]+)/);
      if (match) {
        inviteCode = match[1];
      }
    }

    if (inviteCode) {
      sessionStorage.setItem('pendingInviteCode', inviteCode);
      setAuthMode('register');
    }
  }, []);

  const pixelBackground = {
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 10 10'%3E%3Crect width='2.5' height='2.5' fill='%23000'/%3E%3Crect x='2.5' y='0' width='2.5' height='2.5' fill='%23ccc'/%3E%3Crect x='5' y='0' width='2.5' height='2.5' fill='%23000'/%3E%3Crect x='7.5' y='0' width='2.5' height='2.5' fill='%23ccc'/%3E%3Crect x='0' y='2.5' width='10' height='2.5' fill='%23ccc'/%3E%3Crect x='2.5' y='5' width='2.5' height='2.5' fill='%23000'/%3E%3Crect x='5' y='5' width='2.5' height='2.5' fill='%23ccc'/%3E%3Crect x='7.5' y='5' width='2.5' height='2.5' fill='%23000'/%3E%3Crect x='0' y='5' width='2.5' height='2.5' fill='%23ccc'/%3E%3Crect x='0' y='7.5' width='10' height='2.5' fill='%23ccc'/%3E%3C/svg%3E")`,
    backgroundSize: '12px 12px',
  };

  const containerStyle: React.CSSProperties = {
    minHeight: '100vh',
    backgroundColor: '#ffffff',
    color: '#000000',
    fontFamily: 'Arial, Helvetica, sans-serif',
    margin: 0,
    padding: 0,
    ...pixelBackground,
  };

  const createImageBoxStyle = (
    backgroundColor: string,
    width: string,
    height: string,
    transform?: string,
  ): React.CSSProperties => ({
    width,
    height,
    backgroundColor,
    border: '2px solid #000000',
    boxShadow: '2px 2px 0 #808080',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transform: transform || 'none',
  });

  const handleRegisterComplete = async () => {
    try {
      await register();
    } catch (error) {
      console.error('Registration error:', error);
    }
  };

  if (authMode !== 'home') {
    return (
      <div style={containerStyle}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            padding: '20px',
          }}
        >
          <button
            onClick={() => setAuthMode('home')}
            style={{
              position: 'absolute',
              top: '20px',
              left: '20px',
              fontFamily: 'Tiny5, "Courier New", monospace, sans-serif',
              fontSize: '10px',
              color: '#666',
              cursor: 'pointer',
              textDecoration: 'underline',
              textTransform: 'uppercase',
              background: 'none',
              border: 'none',
            }}
          >
            ← НАЗАД
          </button>

          {authMode === 'register' && <RegisterForm onComplete={handleRegisterComplete} />}
          {authMode === 'login' && <LoginForm onSwitchToRecovery={() => setAuthMode('recovery')} />}
          {authMode === 'recovery' && <RecoveryForm onSwitchToLogin={() => setAuthMode('login')} />}
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div
        style={{
          maxWidth: '400px',
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          paddingTop: '40px',
        }}
      >
        {/* Логотип */}
        <div
          style={{
            alignSelf: 'center',
            marginBottom: '20px',
          }}
        >
          <div
            style={{
              backgroundColor: '#000000',
              color: '#FFFFFF',
              borderColor: '#000000',
            }}
          >
            <div
              style={{
                fontSize: '32px',
                fontWeight: 'bold',
                padding: '4px 16px',
              }}
            >
              Ё
            </div>
          </div>
        </div>

        {/* Основной контент с блоками */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '8px',
            padding: '20px',
          }}
        >
          <div style={{ display: 'flex', gap: '8px' }}>
            <PixelBox
              textColor='#000000'
              backgroundColor='#C0C0C0'
              borderColor='#000000'
              shadowColor='#808080'
              size='large'
              style={{
                height: '54px',
                width: '189px',
                fontFamily: 'Tiny5, "Courier New", monospace, sans-serif',
                fontWeight: 400,
                letterSpacing: '5%',
                fontSize: '24px',
              }}
            >
              АНОНИМНО
            </PixelBox>
            <div style={{ height: '54px', width: '54px' }}>
              <img
                src={MarioImage}
                alt='Mario'
                style={{ maxWidth: '40px', maxHeight: '40px', objectFit: 'contain' }}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                  (e.target as HTMLImageElement).parentElement!.innerHTML = '🍄';
                }}
              />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <div style={{ height: '61px', width: '61px', transform: 'rotate(-13.96deg)', zIndex: 10 }}>
              <div style={{ height: '62px', width: '66px' }}>
                <div style={createImageBoxStyle('#FFF', '66px', '66px')}>
                  <img
                    src={ThunderballImage}
                    alt='Thunder'
                    style={{ maxWidth: '50px', maxHeight: '50px', objectFit: 'contain' }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                      (e.target as HTMLImageElement).parentElement!.innerHTML = '⚡';
                    }}
                  />
                </div>
              </div>
            </div>
            <div
              style={{
                display: 'flex',
                gap: '15px',
                height: '60px',
                alignItems: 'center',
                zIndex: 1,
              }}
            >
              <PixelBox
                textColor='#000000'
                backgroundColor='#C0C0C0'
                borderColor='#000000'
                shadowColor='#808080'
                size='large'
                style={{
                  height: '54px',
                  width: '267px',
                  fontFamily: 'Tiny5, "Courier New", monospace, sans-serif',
                  fontWeight: 400,
                  letterSpacing: '5%',
                  fontSize: '24px',
                }}
              >
                СЛУЧАЙНО
              </PixelBox>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
              <div style={{ display: 'flex', gap: '15px', height: '60px', alignItems: 'center' }}>
                <div style={createImageBoxStyle('#434343', '54px', '54px')}>
                  <img
                    src={CrownImage}
                    alt='Crown'
                    style={{ maxWidth: '40px', maxHeight: '40px', objectFit: 'contain' }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                      (e.target as HTMLImageElement).parentElement!.innerHTML = '👑';
                    }}
                  />
                </div>
                <PixelBox
                  textColor='#000000'
                  backgroundColor='#C0C0C0'
                  borderColor='#000000'
                  shadowColor='#808080'
                  size='large'
                  style={{
                    height: '54px',
                    width: '203px',
                    fontFamily: 'Tiny5, "Courier New", monospace, sans-serif',
                    fontWeight: 400,
                    letterSpacing: '5%',
                    fontSize: '24px',
                  }}
                >
                  БЫСТРО
                </PixelBox>
              </div>
              <div style={{ display: 'flex', gap: '15px', height: '66px', alignItems: 'center' }}>
                <PixelBox
                  textColor='#000000'
                  backgroundColor='#C0C0C0'
                  borderColor='#000000'
                  shadowColor='#808080'
                  size='large'
                  style={{
                    height: '54px',
                    width: '189px',
                    fontFamily: 'Tiny5, "Courier New", monospace, sans-serif',
                    fontWeight: 400,
                    letterSpacing: '5%',
                    fontSize: '24px',
                  }}
                >
                  ПРОСТО
                </PixelBox>
                <div style={{ height: '62px', width: '66px', transform: 'rotate(10.08deg)' }}>
                  <div style={createImageBoxStyle('#898989', '66px', '66px')}>
                    <img
                      src={HeartImage}
                      alt='Heart'
                      style={{ maxWidth: '50px', maxHeight: '50px', objectFit: 'contain' }}
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                        (e.target as HTMLImageElement).parentElement!.innerHTML = '❤️';
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
            <PixelBox
              textColor='#000000'
              backgroundColor='#C0C0C0'
              borderColor='#000000'
              shadowColor='#808080'
              size='large'
              style={{ width: '54px', height: '124px' }}
            >
              <p
                style={{
                  transform: 'rotate(-90deg)',
                  fontFamily: 'Tiny5, "Courier New", monospace, sans-serif',
                  fontWeight: 400,
                  letterSpacing: '5%',
                  fontSize: '24px',
                }}
              >
                KLIC!
              </p>
            </PixelBox>
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
            <PixelBox
              textColor='#FFF'
              backgroundColor='#000'
              borderColor='#000000'
              shadowColor='#808080'
              innerShadowColor={'#000'}
              innerHighlightColor={'#000'}
              size='large'
              style={{ width: '54px', height: '192px' }}
            >
              <p
                style={{
                  transform: 'rotate(-90deg)',
                  fontFamily: 'Tiny5, monospace, sans-serif',
                  fontWeight: 400,
                  letterSpacing: '5%',
                  fontSize: '24px',
                }}
              >
                БЕЗ
              </p>
            </PixelBox>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
              <div style={{ display: 'flex', gap: '15px', height: '66px', alignItems: 'center' }}>
                <PixelBox
                  textColor='#000000'
                  backgroundColor='#C0C0C0'
                  borderColor='#000000'
                  shadowColor='#808080'
                  size='large'
                  style={{
                    height: '54px',
                    width: '262px',
                    fontFamily: 'Tiny5, "Courier New", monospace, sans-serif',
                    fontWeight: 400,
                    letterSpacing: '5%',
                    fontSize: '24px',
                  }}
                >
                  РЕГИСТРАЦИИ
                </PixelBox>
              </div>
              <div style={{ display: 'flex', gap: '8px', height: '66px', alignItems: 'center' }}>
                <div style={createImageBoxStyle('#C0C0C0', '54px', '54px')}>
                  <img
                    src={HandImage}
                    alt='Hand'
                    style={{ maxWidth: '40px', maxHeight: '40px', objectFit: 'contain' }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                      (e.target as HTMLImageElement).parentElement!.innerHTML = '👋';
                    }}
                  />
                </div>
                <PixelBox
                  textColor='#000000'
                  backgroundColor='#C0C0C0'
                  borderColor='#000000'
                  shadowColor='#808080'
                  size='large'
                  style={{
                    height: '54px',
                    width: '133px',
                    fontFamily: 'Tiny5, "Courier New", monospace, sans-serif',
                    fontWeight: 400,
                    letterSpacing: '5%',
                    fontSize: '24px',
                  }}
                >
                  АНКЕТ
                </PixelBox>
              </div>
              <div style={{ display: 'flex', gap: '8px', height: '66px', alignItems: 'center' }}>
                <PixelBox
                  textColor='#000000'
                  backgroundColor='#C0C0C0'
                  borderColor='#000000'
                  shadowColor='#808080'
                  size='large'
                  style={{
                    height: '54px',
                    width: '189px',
                    fontFamily: 'Tiny5, "Courier New", monospace, sans-serif',
                    fontWeight: 400,
                    letterSpacing: '5%',
                    fontSize: '24px',
                  }}
                >
                  ПРОФИЛЕЙ
                </PixelBox>
                <div style={{ height: '56px', width: '56px', paddingTop: '8px' }}>
                  <img
                    src={GhostImage}
                    alt='Ghost'
                    style={{ maxWidth: '56px', maxHeight: '56px', objectFit: 'contain' }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                      (e.target as HTMLImageElement).parentElement!.innerHTML = '👻';
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Кнопки START и LOGIN */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '15px',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <PixelButton
            backgroundColor='#C0C0C0'
            borderColor='#000000'
            shadowColor='#808080'
            onClick={() => setAuthMode('register')}
            size='large'
            style={{
              height: '54px',
              width: '198px',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                fontFamily: 'Tiny5, "Courier New", monospace, sans-serif',
                fontWeight: 400,
                fontSize: '30px',
                letterSpacing: '5%',
              }}
            >
              <span style={{ textTransform: 'none' }}>Start</span>
            </div>
          </PixelButton>

          <div
            onClick={() => setAuthMode('login')}
            style={{
              height: '32px',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <div
              style={{
                alignSelf: 'center',
                fontFamily: 'Tiny5, "Courier New", monospace, sans-serif',
                fontWeight: 400,
                fontSize: '18px',
                letterSpacing: '5%',
                color: 'white',
                textShadow: `
                  2px 2px 0 #000, 
                  -2px -2px 0 #000, 
                  2px -2px 0 #000, 
                  -2px 2px 0 #000,
                  3px 3px 0 #000
                `,
              }}
            >
              ВХОД
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
